// The account menu (FR-024, FR-047; research R14): the initials button opens a menu with Profile
// (parent mode) and Log out. Web keyboard: the button has aria-haspopup="menu" and aria-expanded;
// opening focuses the first item; arrow keys move focus; Escape closes and returns focus. 002 US4
// adds Switch role and Refresh access to the same list.
import { useRouter } from "expo-router";
import { type Ref, useEffect, useRef, useState } from "react";
import { Platform, StyleSheet, View } from "react-native";

import { AUTH_COPY } from "@/auth/copy";
import { effectiveRole } from "@/session/access";
import { useSession, useSessionController } from "@/session/hooks";
import { usePlatform } from "@/session/platform";
import { makeStyles } from "@/theme/makeStyles";
import { PressableTarget } from "@/ui/PressableTarget";
import { Text } from "@/ui/Text";

import { runGuarded } from "./leaveGuard";
import { useRoleActions } from "./useRoleActions";
import type { ThemePreference } from "@/theme/theme";
import { useTheme } from "@/theme/useTheme";

type Focusable = { focus?: () => void };
type Item = { id: string; label: string; onSelect: () => void };

const isWeb = Platform.OS === "web";

const THEME_OPTIONS: readonly { value: ThemePreference; label: string }[] = [
  { value: "system", label: "System" },
  { value: "light", label: "Light" },
  { value: "dark", label: "Dark" },
];
const web = (props: Record<string, unknown>) => (isWeb ? props : {});

const useStyles = makeStyles((t) =>
  StyleSheet.create({
    anchor: { position: "relative" },
    avatar: {
      width: t.size.avatar,
      height: t.size.avatar,
      borderRadius: t.radius.mark,
      backgroundColor: t.color.accent,
      alignItems: "center",
      justifyContent: "center",
    },
    initials: { color: t.color.onAccent },
    menu: {
      position: "absolute",
      top: t.size.touchTarget + t.space.xxs,
      right: 0,
      minWidth: 200,
      paddingVertical: t.space.xxs,
      backgroundColor: t.color.surface,
      borderColor: t.color.border,
      borderWidth: t.borderWidth.card,
      borderRadius: t.radius.panel,
      boxShadow: t.shadow.card,
      zIndex: 10,
    },
    item: {
      alignItems: "flex-start",
      paddingHorizontal: t.space.lg,
      borderRadius: 0,
    },
    // 006 (FR-006): the name line and the theme choice in school-admin mode.
    nameLine: {
      paddingHorizontal: t.space.lg,
      paddingVertical: t.space.sm,
      borderBottomWidth: t.borderWidth.card,
      borderBottomColor: t.color.border,
    },
    themeGroup: {
      paddingHorizontal: t.space.lg,
      paddingVertical: t.space.xs,
      borderTopWidth: t.borderWidth.card,
      borderTopColor: t.color.border,
    },
    themeRow: { flexDirection: "row", gap: t.space.xxs },
    themeOption: {
      paddingHorizontal: t.space.sm,
      borderRadius: t.radius.buttonSm,
    },
    themeSelected: { backgroundColor: t.color.primarySoft },
  }),
);

export function AccountMenu() {
  const styles = useStyles();
  const session = useSession();
  const platform = usePlatform();
  const router = useRouter();
  const { signOut } = useSessionController();
  const roleActions = useRoleActions();
  const [open, setOpen] = useState(false);
  const button = useRef<Focusable>(null);
  const itemRefs = useRef<(Focusable | null)[]>([]);

  const parentMode =
    session.status === "signedIn" &&
    effectiveRole(session, platform) === "PARENT";
  const initials = session.status === "signedIn" ? session.user.initials : "";
  const schoolAdminMode =
    session.status === "signedIn" &&
    effectiveRole(session, platform) === "SCHOOL_ADMIN";
  const { preference, setPreference } = useTheme();

  const close = (refocus: boolean) => {
    setOpen(false);
    if (refocus) button.current?.focus?.();
  };

  const items: Item[] = [
    ...(parentMode
      ? [
          {
            id: "profile",
            label: "Profile",
            onSelect: () => router.push("/profile"),
          },
        ]
      : []),
    ...roleActions,
    {
      id: "logout",
      label: AUTH_COPY.actions.logOut,
      onSelect: () => {
        // 006 FR-014: unsaved class changes ask first. Same event as the navigation, so no guard
        // adds a returnTo for a deliberate log-out.
        void runGuarded(() => {
          void signOut("USER");
          router.replace("/welcome");
        });
      },
    },
  ];

  useEffect(() => {
    if (open) itemRefs.current[0]?.focus?.();
  }, [open]);

  const onKeyDown = (event: { key: string; preventDefault: () => void }) => {
    const current = itemRefs.current.findIndex(
      (el) => isWeb && el === (globalThis.document?.activeElement as unknown),
    );
    const move = (index: number) => {
      event.preventDefault();
      itemRefs.current[(index + items.length) % items.length]?.focus?.();
    };
    if (event.key === "ArrowDown") move(current + 1);
    else if (event.key === "ArrowUp") move(current - 1);
    else if (event.key === "Home") move(0);
    else if (event.key === "End") move(items.length - 1);
    else if (event.key === "Escape") {
      event.preventDefault();
      close(true);
    }
  };

  return (
    <View style={styles.anchor}>
      <PressableTarget
        ref={button as Ref<View>}
        accessibilityLabel="Account"
        aria-expanded={open}
        {...web({ "aria-haspopup": "menu" })}
        onPress={() => setOpen((o) => !o)}
        testID="account-menu-button"
      >
        <View style={styles.avatar}>
          <Text
            variant="heading"
            weight="semiBold"
            size="sm"
            style={styles.initials}
          >
            {initials}
          </Text>
        </View>
      </PressableTarget>
      {open ? (
        <View
          style={styles.menu}
          role="menu"
          aria-label="Account"
          testID="account-menu"
          {...(isWeb ? { onKeyDown } : {})}
        >
          {schoolAdminMode && session.status === "signedIn" ? (
            <View style={styles.nameLine} testID="account-menu-name">
              <Text weight="semiBold">{session.user.displayName}</Text>
            </View>
          ) : null}
          {items.map((item, index) => (
            <PressableTarget
              key={item.id}
              ref={(el: View | null) => {
                itemRefs.current[index] = el as Focusable | null;
              }}
              accessibilityRole="menuitem"
              accessibilityLabel={item.label}
              onPress={() => {
                close(false);
                item.onSelect();
              }}
              style={styles.item}
            >
              <Text>{item.label}</Text>
            </PressableTarget>
          ))}
          {schoolAdminMode ? (
            <View
              style={styles.themeGroup}
              role="radiogroup"
              aria-label="Theme"
              testID="account-menu-theme"
            >
              <Text size="xs" tone="muted">
                Theme
              </Text>
              <View style={styles.themeRow}>
                {THEME_OPTIONS.map(({ value, label }) => {
                  const selected = preference === value;
                  return (
                    <PressableTarget
                      key={value}
                      accessibilityRole="radio"
                      accessibilityLabel={label}
                      aria-checked={selected}
                      testID={`account-theme-${value}`}
                      style={[
                        styles.themeOption,
                        selected && styles.themeSelected,
                      ]}
                      onPress={() => setPreference(value)}
                    >
                      <Text size="sm">{label}</Text>
                    </PressableTarget>
                  );
                })}
              </View>
            </View>
          ) : null}
        </View>
      ) : null}
    </View>
  );
}
